import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';

import styled from 'styled-components';

import {
  getCommunity,
  getCompetitors,
  getContractBenefits,
  getFees,
  getFeesIncentives,
  getLivingInfo,
} from 'apis/CommunityAPI';
import Alert from 'components/lib/Alert';
import { hasPermissionOrAdmin } from 'helpers/permissions';
import { SNAPSHOT_EDIT_PERMISSIONS } from 'permissions/snapshots';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';
import { getDisplayUnitSource } from 'utils/helpers/units';

import { CareDataType, CommunityFeesType, LivingInfoGroupedDataType } from './careTypes/types';
import { groupLivingInfoByApartmentType } from './careTypes/utils';
import CommunityInfoContent from './CommunityInfoContent';
import { CommunityInfoContext } from './CommunityInfoContext';
import CommunityInfoHeader from './CommunityInfoHeader';
import { HistoryItemDataType } from './types';
import { canShowCommunityInfoPricingData } from './utils';
import { useSnapshotContext } from '../../common/Snapshot/SnapshotContext';
import { COMMUNITY_INFO_MODE } from '../../constants/community';
import { getSnapshotReminder } from '../View/utils';

const Container = styled.div`
  width: 100%;
  overflow: auto;
`;

const Content = styled.div`
  padding: 0 20px 20px 20px;
  overflow: auto;
  .ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    font-family: var(--font-bold);
  }
  .ant-tabs-tab-btn {
    color: var(--text-secondary);
  }
`;

const CommunityInfo = () => {
  const careTypes = useAppSelector((state) => state.global.livingTypes);
  const { currentUser } = useAppSelector((state) => state.auth);
  const { selectedCommunity, selectedParentCommunity } = useAppSelector((state) => state.global);
  const { snapshot } = useSnapshotContext();

  const [communityInfo, setCommunityInfo] = useState<CommunityType>();
  const [mappedCareData, setMappedCareData] = useState<CareDataType[]>([]);

  const [history, setHistory] = useState<HistoryItemDataType | undefined>(undefined);

  const { communityId, competitorId } = useParams();
  const id = Number(competitorId || communityId);

  const navigate = useNavigate();

  const {
    isLoading: communityLoading,
    isSuccess: communityFetched,
    refetch: communityInfoRefetch,
  } = useQuery({
    queryKey: ['community', 'basic', 'info', id],
    queryFn: () => getCommunity(id),
    onSuccess: (data) => {
      setCommunityInfo(data);
    },
    enabled: Boolean(id),
  });

  const { data: communityFees, isSuccess: communityFeesFetched } = useQuery({
    queryKey: ['community', 'feesIncentives', id, snapshot],
    queryFn: () => getFeesIncentives(id, { snapshot: snapshot?.uuid }),
    enabled: Boolean(id) && Boolean(snapshot),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: fees, isSuccess: careFeesFetched } = useQuery({
    queryKey: ['community', 'fees', id, snapshot],
    queryFn: () => getFees(id, { snapshot: snapshot?.uuid }),
    enabled: Boolean(id) && Boolean(snapshot),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: livingInfos, isSuccess: livingInfosFetched } = useQuery({
    queryKey: ['community', 'livingInfo', id, snapshot],
    queryFn: () => getLivingInfo(id, { snapshot: snapshot?.uuid }),
    enabled: Boolean(id) && Boolean(snapshot),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: contractBenefits } = useQuery({
    queryKey: ['community', 'contractBenefits', id, snapshot],
    queryFn: () => getContractBenefits(id, { snapshot: snapshot?.uuid }),
    enabled: Boolean(id) && Boolean(snapshot),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: competitors } = useQuery({
    queryKey: ['community', 'competitors', id],
    queryFn: () => getCompetitors(id),
    enabled: Boolean(id && currentUser?.isSuperuser),
    refetchOnWindowFocus: false,
  });

  const handleUpdateNow = () => {
    navigate({
      pathname: location.pathname,
      search: '?openSnapshotEditor=1',
    });
  };

  useEffect(() => {
    if (
      !communityLoading &&
      communityFetched &&
      careTypes &&
      careFeesFetched &&
      communityFeesFetched &&
      livingInfosFetched
    ) {
      const mappedData = careTypes.map((careType) => {
        const careTypeCommunityFees: CommunityFeesType[] =
          communityFees?.filter((fee: CommunityFeesType) => fee.living_type_id === careType.id) || [];

        const livingTypeFees = fees?.filter((fee: any) => fee.living_type_id === careType.id) || [];

        const livingInfoUnGroupData = livingInfos?.filter((info: any) => info.living_type_id === careType.id) || [];
        const crateTypeLivingInfo: LivingInfoGroupedDataType[] = groupLivingInfoByApartmentType(livingInfoUnGroupData);

        return {
          ...careType,
          communityFees: careTypeCommunityFees,
          fees: livingTypeFees,
          livingInfo: crateTypeLivingInfo,
        };
      });

      setMappedCareData(mappedData);
    }
  }, [fees, communityFees, livingInfos, snapshot]);

  const communityInfoMode = !selectedCommunity ? COMMUNITY_INFO_MODE.SYSTEM : COMMUNITY_INFO_MODE.PRIVATE;
  const showPricingData = canShowCommunityInfoPricingData(currentUser, communityInfoMode);
  const unitSource = getDisplayUnitSource(selectedCommunity, selectedParentCommunity) ?? communityInfo;

  const snapshotReminder = getSnapshotReminder(communityInfo?.latest_snapshot_date);
  const showSnapshotReminder =
    showPricingData && communityInfo && snapshotReminder && !competitorId && !currentUser?.isSuperuser;
  const showUpdateButton = showPricingData && hasPermissionOrAdmin(currentUser, SNAPSHOT_EDIT_PERMISSIONS);
  const alertActions = showUpdateButton
    ? [{ dataTestId: 'banner_update_pricing_update_now_link', label: 'Update now', onClick: handleUpdateNow }]
    : [];

  return (
    <CommunityInfoContext.Provider
      value={{
        loaded: communityFetched,
        mode: communityInfoMode,
        communityInfo,
        unitSource,
        isCompetitor: Boolean(competitorId),
        history,
        mappedCareData,
        contractBenefits,
        competitors,
        setCommunityInfo,
        setHistory,
        setMappedCareData,
        communityInfoRefetch,
      }}
    >
      <Container data-test-id="cd-community">
        {showSnapshotReminder && (
          <Alert
            dataTestId="banner_update_pricing_text"
            type={snapshotReminder.type}
            message={snapshotReminder.message}
            showIcon
            actions={alertActions}
          />
        )}
        <CommunityInfoHeader />
        <Content data-test-id="cd-community-info-content">
          <CommunityInfoContent />
        </Content>
      </Container>
    </CommunityInfoContext.Provider>
  );
};

export default CommunityInfo;
